<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">异常注单管理</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <router-link to="/admin/bets" class="text-gray-600 hover:text-gray-800">注单管理</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">异常注单</span>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100 text-red-600">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">未解决异常</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.unresolved }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">较昨日</p>
            <p :class="['text-sm font-semibold', stats.unresolved_change > 0 ? 'text-red-600' : 'text-green-600']">
              {{ stats.unresolved_change > 0 ? '+' : '' }}{{ stats.unresolved_change }}%
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">系统错误</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.system_errors }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">本月</p>
            <p class="text-sm text-yellow-600 font-semibold">{{ stats.system_errors_percent }}%</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">已解决异常</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.resolved }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">解决率</p>
            <p class="text-sm text-green-600 font-semibold">{{ stats.resolution_rate }}%</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">今日新增</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.today_new }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">较昨日</p>
            <p :class="['text-sm font-semibold', stats.today_change > 0 ? 'text-red-600' : 'text-green-600']">
              {{ stats.today_change > 0 ? '+' : '' }}{{ stats.today_change }}%
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选器 -->
    <SearchFilters 
      :filter-config="filterConfig"
      :initial-filters="filters"
      @search="search"
      @reset="resetFilters"
      @update:filters="updateFilters"
      ref="searchFiltersRef"
    />

    <!-- 异常注单表格 -->
    <div class="bg-white shadow rounded-md overflow-hidden">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">异常注单列表</h3>
        <div>
          <button @click="exportAbnormalBets" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            导出
          </button>
        </div>
      </div>
      
      <TableList
        :columns="columns"
        :items="abnormalBets"
        :loading="loading"
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        :initial-sort-key="sortBy"
        :initial-sort-order="sortDirection"
        empty-text="暂无异常注单数据"
        @page-change="handlePageChange"
        @sort="handleSort"
      >
        <!-- 自定义单元格渲染 -->
        <template #cell-fundsn="{ item }">
          <div class="flex items-center space-x-1">
            <span class="font-medium">{{ item.fundsn }}</span>
            <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
              异常
            </span>
          </div>
        </template>

        <template #cell-vendor_username="{ item }">
          <a 
            href="#" 
            @click.prevent="viewPlayerDetails(item.user_id)"
            class="text-indigo-600 hover:text-indigo-900"
          >
            {{ item.vendor_username }}
          </a>
        </template>

        <template #cell-anomaly_info="{ item }">
          <div class="flex flex-col">
            <span class="text-gray-800 font-medium">{{ getAbnormalReasonText(item.reason) }}</span>
            <span class="text-xs text-gray-500 truncate max-w-xs">{{ item.description || '-' }}</span>
          </div>
        </template>

        <template #cell-created_at="{ item }">
          {{ formatDatetime(item.created_at) }}
        </template>

        <template #cell-resolved_status="{ item }">
          <span 
            :class="[
              'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
              item.resolved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            ]"
          >
            {{ item.resolved ? '已解决' : '未解决' }}
          </span>
        </template>

        <template #cell-actions="{ item }">
          <div class="flex items-center space-x-2">
            <button
              @click="viewBetDetail(item)"
              class="text-blue-600 hover:text-blue-900"
              title="查看详情"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </button>
            <button
              v-if="!item.resolved"
              @click="resolveAbnormal(item.id, item.fundsn)"
              class="text-green-600 hover:text-green-800"
              title="解决异常"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </button>
          </div>
        </template>
      </TableList>
    </div>

    <!-- 模态框引用 -->
    <BetDetailsModal
      :visible="showBetDetailModal"
      :bet-data="currentBetData"
      @close="closeBetDetailModal"
      @abnormal-updated="handleAbnormalUpdated"
      @mark-resolved="handleMarkResolved"
      @bet-updated="handleBetUpdated"
    />

    <ResolveAbnormalModal
      :visible="showResolveModal"
      :abnormal-id="currentAbnormalId"
      :fundsn="currentFundsn"
      @close="closeResolveModal"
      @submit-success="handleResolveSubmitted"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import AdminApiClient from '@/utils/admin-api-client';
import SearchFilters from '@/components/common/SearchFilters.vue';
import TableList from '@/components/common/TableList.vue';
import BetDetailsModal from '@/components/bets/BetDetailsModal.vue';
import ResolveAbnormalModal from '@/components/bets/ResolveAbnormalModal.vue';

const router = useRouter();
const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const abnormalBets = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const totalPages = ref(1);
const itemsPerPage = ref(15);
const sortBy = ref('created_at');
const sortDirection = ref('desc');

// 统计数据
const stats = reactive({
  unresolved: 0,
  unresolved_change: 0,
  system_errors: 0,
  system_errors_percent: 0,
  resolved: 0,
  resolution_rate: 0,
  today_new: 0,
  today_change: 0
});

// 筛选器状态
const filters = reactive({
  reason: '',
  resolved: '',
  fundsn: '',
  system: '',
  start_time: '',
  end_time: ''
});

// 搜索过滤组件引用
const searchFiltersRef = ref(null);

// 选项数据
const reasonOptions = ref([
  { value: 'system_error', label: '系统错误' },
  { value: 'amount_error', label: '金额错误' },
  { value: 'betting_anomaly', label: '投注异常' },
  { value: 'other_issue', label: '其他问题' }
]);

const resolvedOptions = ref([
  { value: '0', label: '未解决' },
  { value: '1', label: '已解决' }
]);

// 注单详情模态框状态
const showBetDetailModal = ref(false);
const currentBetData = ref({});

// 解决异常模态框状态
const showResolveModal = ref(false);
const currentAbnormalId = ref('');
const currentFundsn = ref('');
const currentSystem = ref('');

// 表格列配置
const columns = [
  { key: 'fundsn', label: '注单ID', sortable: true },
  { key: 'system', label: '游戏平台', sortable: true },
  { key: 'anomaly_info', label: '异常原因', sortable: false },
  { key: 'created_at', label: '标记时间', sortable: true },
  { key: 'admin_name', label: '标记管理员', sortable: true },
  { key: 'resolved_status', label: '状态', sortable: true },
  { key: 'actions', label: '操作', sortable: false }
];

// 过滤器配置
const filterConfig = ref([
  {
    id: 'reason',
    type: 'select',
    label: '异常类型',
    placeholder: '全部类型',
    options: reasonOptions,
    icon: 'fas fa-exclamation-triangle'
  },
  {
    id: 'resolved',
    type: 'select',
    label: '状态',
    placeholder: '全部状态',
    options: resolvedOptions,
    icon: 'fas fa-check-circle'
  },
  {
    id: 'fundsn',
    type: 'input',
    label: '注单ID',
    placeholder: '输入注单ID',
    icon: 'fas fa-receipt'
  },
  {
    id: 'system',
    type: 'input',
    label: '游戏系统',
    placeholder: '输入游戏系统',
    icon: 'fas fa-gamepad'
  },
  {
    id: 'date_range',
    type: 'dateRange',
    label: '标记日期',
    placeholder: '选择日期范围',
    icon: 'fas fa-calendar',
    startField: 'start_time',
    endField: 'end_time'
  }
]);

// 生命周期钩子
onMounted(() => {
  loadAbnormalBetsData();
});

// 加载异常注单数据
async function loadAbnormalBetsData(page) {
  if (loading.value) return;
  
  loading.value = true;
  
  // 如果提供了页码，更新当前页
  if (page) {
    currentPage.value = Number(page);
  }
  
  try {
    const params = {
      page: currentPage.value,
      per_page: itemsPerPage.value,
      ...getApiFilters()
    };
    
    const response = await apiClient.getAbnormalBets(params);
    if (response && response.success) {
      const data = response.data || {};
      
      // 更新表格数据
      abnormalBets.value = data.items || [];
      totalItems.value = Number(data.total) || 0;
      totalPages.value = Math.ceil(totalItems.value / itemsPerPage.value);
      currentPage.value = Number(data.page) || 1;
      
      // 处理每一个注单项，确保数据格式正确
      abnormalBets.value = abnormalBets.value.map(bet => ({
        ...bet,
        resolved: !!bet.resolved, // 确保是布尔值
        created_at: bet.created_at || '', // 确保有创建时间
        resolved_at: bet.resolved_at || null,
        admin_name: bet.admin_name || '系统',
        description: bet.description || '',
        vendor_username: bet.vendor_username || '未知用户'
      }));
      
      // 更新统计数据
      if (data.stats) {
        Object.keys(stats).forEach(key => {
          if (data.stats[key] !== undefined) {
            stats[key] = data.stats[key];
          }
        });
        
        // 计算百分比
        stats.resolution_rate = stats.total > 0 ? Math.round((stats.resolved / stats.total) * 100) : 0;
        stats.system_errors_percent = stats.total > 0 ? Math.round((stats.system_errors / stats.total) * 100) : 0;
      }
    } else {
      abnormalBets.value = [];
      console.error('Failed to load abnormal bets:', response?.message || 'Unknown error');
    }
  } catch (error) {
    console.error('Failed to load abnormal bets:', error);
    abnormalBets.value = [];
  } finally {
    loading.value = false;
  }
}

// 获取API过滤参数
function getApiFilters() {
  const apiFilters = {};
  
  // 复制非空筛选条件
  Object.keys(filters).forEach(key => {
    if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
      apiFilters[key] = filters[key];
    }
  });
  
  return apiFilters;
}

// 搜索按钮处理程序
function search(searchFilters) {
  Object.keys(searchFilters).forEach(key => {
    filters[key] = searchFilters[key];
  });
  currentPage.value = 1;
  loadAbnormalBetsData(1);
}

// 重置筛选条件
function resetFilters(resetFilters) {
  Object.keys(resetFilters).forEach(key => {
    filters[key] = resetFilters[key];
  });
  
  currentPage.value = 1;
  loadAbnormalBetsData(1);
}

// 更新过滤器
function updateFilters(newFilters) {
  Object.keys(newFilters).forEach(key => {
    filters[key] = newFilters[key];
  });
}

// 格式化日期时间显示
function formatDatetime(datetime) {
  if (!datetime) return '-';
  
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return datetime;
  
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).replace(/\//g, '-');
}

// 获取异常原因文本
function getAbnormalReasonText(reason) {
  const reasonMap = {
    'system_error': '系统错误',
    'amount_error': '金额错误',
    'betting_anomaly': '投注异常',
    'other_issue': '其他问题'
  };
  
  return reasonMap[reason] || reason || '未知';
}

// 导出异常注单
function exportAbnormalBets() {
  try {
    const apiFilters = getApiFilters();
    const queryParams = new URLSearchParams();
    
    // 添加筛选条件
    Object.keys(apiFilters).forEach(key => {
      if (apiFilters[key] !== null && apiFilters[key] !== undefined && apiFilters[key] !== '') {
        queryParams.append(key, apiFilters[key]);
      }
    });
    
    // 构建导出URL，实际使用时请替换为正确的API路径
    const exportUrl = `/admin/api/bets/abnormal/export?${queryParams.toString()}`;
    window.open(exportUrl, '_blank');
  } catch (error) {
    console.error('导出异常注单失败:', error);
    alert('导出失败，请重试');
  }
}

// 查看注单详情
async function viewBetDetail(item) {
  try {
    currentBetData.value = {
      id: item.bet_id,
      fundsn: item.fundsn,
      system: item.system,
      loading: true,
      is_abnormal: true
    };
    let params = {
      fundsn: item.fundsn,
      system: item.system,
      bet_id: item.bet_id,
      abnormal_id: item.abnormal_id
    }
    
    // 显示详情模态框
    showBetDetailModal.value = true;
    
    // 如果有API方法，尝试加载详细数据
    if (apiClient.getBetDetails) {
      const response = await apiClient.getBetDetails(params);
      if (response && response.success) {
        currentBetData.value = { 
          ...response.data,
          abnormal_reason:response.data.abnormal_info.reason_text,
          abnormal_description:response.data.abnormal_info.description,
          abnormal_id:response.data.abnormal_info.id,
          abnormal_created_at:response.data.abnormal_info.created_at,
          abnormal_admin_name:response.data.abnormal_info.admin_name,
          is_abnormal: true  // 保持异常标记
        };
      }
    } else {
      // 模拟数据
      setTimeout(() => {
        currentBetData.value = {
          id: betId,
          fundsn: fundsn,
          system: system,
          bet_amount: 100,
          win_amount: 0,
          payout: 0,
          status: 'completed',
          created_at: new Date().toISOString(),
          settled_at: null,
          user_id: 101,
          username: 'player123',
          game_type: 'slot',
          game_name: 'Lucky Dragon',
          is_abnormal: true
        };
      }, 500);
    }
  } catch (error) {
    console.error('Failed to load bet details:', error);
    alert('加载注单详情失败，请重试');
  }
}

// 关闭注单详情模态框
function closeBetDetailModal() {
  showBetDetailModal.value = false;
  currentBetData.value = {};
}

// 解决异常
function resolveAbnormal(abnormalId, fundsn) {
  currentAbnormalId.value = abnormalId;
  currentFundsn.value = fundsn;
  showResolveModal.value = true;
}

// 关闭解决异常模态框
function closeResolveModal() {
  showResolveModal.value = false;
}

// 处理解决异常提交成功
function handleResolveSubmitted() {
  // 重新加载数据
  loadAbnormalBetsData(currentPage.value);
  
  // 显示成功提示
  alert('异常已成功解决');
}

// 处理页码变化
function handlePageChange(page) {
  if (page < 1 || page > totalPages.value || loading.value) {
    return;
  }
  
  loadAbnormalBetsData(page);
}

// 处理排序变化
function handleSort({ key, direction }) {
  sortBy.value = key;
  sortDirection.value = direction;
  loadAbnormalBetsData(1);
}

// 查看玩家详情
function viewPlayerDetails(userId) {
  if (!userId) return;
  router.push(`/admin/players/${userId}`);
}

// 处理异常信息更新
async function handleAbnormalUpdated(data) {
  console.log('异常信息已更新:', data);
  
  // 更新当前显示的注单列表中对应的项
  const index = abnormalBets.value.findIndex(bet => 
    bet.id === currentAbnormalId.value || 
    bet.abnormal_id === data.id
  );
  
  if (index !== -1) {
    // 更新异常原因显示
    abnormalBets.value[index].reason = data.reason;
    abnormalBets.value[index].description = data.description;
    
    // 如果需要，可以重新加载整个列表
    // await loadAbnormalBetsData(currentPage.value);
  }
}

// 处理标记为已解决
async function handleMarkResolved(abnormalId) {
  if (!abnormalId) {
    console.error('标记为已解决失败：缺少异常ID');
    throw new Error('缺少异常ID');
  }

  // 重新加载数据
  await loadAbnormalBetsData(currentPage.value);
}

// 处理注单更新
async function handleBetUpdated(data) {
  console.log('注单信息已更新:', data);
  
  // 重新加载注单数据以确保显示最新状态
  await loadAbnormalBetsData(currentPage.value);
}
</script>

<style scoped>
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-radius: 50%;
  border-top: 3px solid #1aaa55;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 确保模态框层级足够高 */
:deep(.modal-overlay) {
  z-index: 9999 !important;
  position: fixed !important;
  inset: 0 !important;
}

:deep(.modal-container) {
  max-width: 90vw;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
</style> 